import { PfeilLinksIcon } from '../icons';
import { ZUSTAND_LABEL, type Objekt, type Protokoll, type Raum } from '../domain/types';

interface VergleichProps {
	objekt: Objekt;
	einzug: Protokoll;
	auszug: Protokoll;
	onZurueck: () => void;
}

function raumNachName(raeume: Raum[], name: string): Raum | undefined {
	return raeume.find((r) => r.name.toLowerCase() === name.toLowerCase());
}

/**
 * Reine Gegenüberstellung, bewusst OHNE automatische "das ist neu"-Erkennung:
 * Zwei Freitext-Mängel als "denselben Schaden" oder "neuen Schaden"
 * einzustufen ist software-seitig nicht verlässlich genug für eine Frage,
 * bei der es im Zweifel um die Kaution geht (siehe AGENTS.md — kein
 * Rechtsberatungs-Anspruch). Der Mensch vergleicht, die App legt nur
 * nebeneinander, was an Einzug und Auszug erfasst wurde.
 */
export function Vergleich({ objekt, einzug, auszug, onZurueck }: VergleichProps) {
	const raumNamen = Array.from(
		new Set([...einzug.raeume.map((r) => r.name), ...auszug.raeume.map((r) => r.name)])
	);

	return (
		<div className="schritt schritt--vergleich">
			<div className="vergleich-werkzeuge nicht-drucken">
				<button type="button" className="nebenknopf" onClick={onZurueck}>
					<PfeilLinksIcon width={18} height={18} />
					Zurück
				</button>
			</div>

			<div className="vergleich-dokument">
				<header className="dokument-kopf">
					<h1>Vergleich Einzug ↔ Auszug</h1>
					<p>
						{objekt.strasse}
						{objekt.plz || objekt.ort ? `, ${objekt.plz} ${objekt.ort}`.trim() : ''}
					</p>
					<p className="dokument-meta">
						Einzug {new Date(einzug.erstelltAm).toLocaleDateString('de-DE')} · Auszug{' '}
						{new Date(auszug.erstelltAm).toLocaleDateString('de-DE')}
					</p>
				</header>

				{raumNamen.length === 0 && <p className="dokument-ohne-befund">Keine Räume erfasst.</p>}

				{raumNamen.map((name) => {
					const raumEinzug = raumNachName(einzug.raeume, name);
					const raumAuszug = raumNachName(auszug.raeume, name);
					return (
						<section key={name} className="vergleich-raum">
							<h3>{name}</h3>
							<div className="vergleich-spalten">
								<div className="vergleich-spalte">
									<p className="vergleich-spalte-titel">Einzug</p>
									{!raumEinzug && <p className="dokument-ohne-befund">Nicht erfasst</p>}
									{raumEinzug && raumEinzug.maengel.length === 0 && (
										<p className="dokument-ohne-befund">Keine Auffälligkeiten</p>
									)}
									{raumEinzug?.maengel.map((m) => (
										<div key={m.id} className="vergleich-mangel">
											<strong>{m.gegenstand}</strong> — {m.beschreibung}
											<span className={`dokument-zustand dokument-zustand--${m.zustand}`}>{ZUSTAND_LABEL[m.zustand]}</span>
										</div>
									))}
								</div>
								<div className="vergleich-spalte">
									<p className="vergleich-spalte-titel">Auszug</p>
									{!raumAuszug && <p className="dokument-ohne-befund">Nicht erfasst</p>}
									{raumAuszug && raumAuszug.maengel.length === 0 && (
										<p className="dokument-ohne-befund">Keine Auffälligkeiten</p>
									)}
									{raumAuszug?.maengel.map((m) => (
										<div key={m.id} className="vergleich-mangel">
											<strong>{m.gegenstand}</strong> — {m.beschreibung}
											<span className={`dokument-zustand dokument-zustand--${m.zustand}`}>{ZUSTAND_LABEL[m.zustand]}</span>
										</div>
									))}
								</div>
							</div>
						</section>
					);
				})}

				<p className="vergleich-hinweis">
					Diese Gegenüberstellung ordnet nichts automatisch als „neuen Schaden" ein — das entscheiden die
					Beteiligten anhand der Einträge.
				</p>
			</div>
		</div>
	);
}
